import { PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';

import { api } from 'services';
import { responseErrors } from 'services/http/responseErrors';

import {
  advertisingInitState,
  getAdvertisingChannelsRequest,
  getAdvertisingChannelsSuccess,
  getAdvertisingDashboardRequest,
  getAdvertisingDashboardSuccess,
  getAdvertisingPostRequest,
  getAdvertisingPostsRequest,
  getAdvertisingPostsSuccess,
  getAdvertisingPostSuccess,
  getAdvertisingReportsRequest,
  getAdvertisingReportsSuccess,
  getAdvertisingRevenueEventRequest,
  getAdvertisingRevenueEventsRequest,
  getAdvertisingRevenueEventsSuccess,
  getAdvertisingRevenueEventSuccess,
  getAdvertisingWalletCreditRequest,
  getAdvertisingWalletCreditsRequest,
  getAdvertisingWalletCreditsSuccess,
  getAdvertisingWalletCreditSuccess,
} from './reducer';

import {
  IAdvertisingChannelsRequest,
  IAdvertisingChannelsResponse,
  IAdvertisingDashboardRequest,
  IAdvertisingDashboardResponse,
  IAdvertisingPostDetailRequest,
  IAdvertisingPostDetailResponse,
  IAdvertisingPostsRequest,
  IAdvertisingPostsResponse,
  IAdvertisingReportsRequest,
  IAdvertisingRevenueEventDetailRequest,
  IAdvertisingRevenueEventDetailResponse,
  IAdvertisingRevenueEventsRequest,
  IAdvertisingRevenueEventsResponse,
  IAdvertisingWalletCreditDetailRequest,
  IAdvertisingWalletCreditDetailResponse,
  IAdvertisingWalletCreditsRequest,
  IAdvertisingWalletCreditsResponse,
} from './types';

function* getChannelsWorker({ payload }: PayloadAction<IAdvertisingChannelsRequest | undefined>) {
  try {
    const response: IAdvertisingChannelsResponse = yield call(api.advertising.getChannels, payload);

    yield put(getAdvertisingChannelsSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getDashboardWorker({ payload }: PayloadAction<IAdvertisingDashboardRequest | undefined>) {
  try {
    const response: IAdvertisingDashboardResponse = yield call(
      api.advertising.getDashboard,
      payload,
    );

    yield put(getAdvertisingDashboardSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getPostsWorker({ payload }: PayloadAction<IAdvertisingPostsRequest | undefined>) {
  try {
    const response: IAdvertisingPostsResponse = yield call(api.advertising.getPosts, payload);

    yield put(getAdvertisingPostsSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getPostWorker({ payload }: PayloadAction<IAdvertisingPostDetailRequest>) {
  try {
    const response: IAdvertisingPostDetailResponse = yield call(
      api.advertising.getPost,
      payload.post_id,
    );

    yield put(getAdvertisingPostSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getReportsWorker({ payload }: PayloadAction<IAdvertisingReportsRequest>) {
  try {
    const response: Blob = yield call(api.advertising.getReports, payload);

    yield put(getAdvertisingReportsSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getRevenueEventsWorker({
  payload,
}: PayloadAction<IAdvertisingRevenueEventsRequest | undefined>) {
  try {
    const response: IAdvertisingRevenueEventsResponse = yield call(
      api.advertising.getRevenueEvents,
      payload,
    );

    yield put(getAdvertisingRevenueEventsSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getRevenueEventWorker({ payload }: PayloadAction<IAdvertisingRevenueEventDetailRequest>) {
  try {
    const response: IAdvertisingRevenueEventDetailResponse = yield call(
      api.advertising.getRevenueEvent,
      payload.event_id,
    );

    yield put(getAdvertisingRevenueEventSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getWalletCreditsWorker({
  payload,
}: PayloadAction<IAdvertisingWalletCreditsRequest | undefined>) {
  try {
    const response: IAdvertisingWalletCreditsResponse = yield call(
      api.advertising.getWalletCredits,
      payload,
    );

    yield put(getAdvertisingWalletCreditsSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

function* getWalletCreditWorker({ payload }: PayloadAction<IAdvertisingWalletCreditDetailRequest>) {
  try {
    const response: IAdvertisingWalletCreditDetailResponse = yield call(
      api.advertising.getWalletCredit,
      payload.transaction_id,
    );

    yield put(getAdvertisingWalletCreditSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(advertisingInitState());
  }
}

export function* advertisingSaga() {
  yield takeEvery(getAdvertisingChannelsRequest.type, getChannelsWorker);

  yield takeEvery(getAdvertisingDashboardRequest.type, getDashboardWorker);

  yield takeEvery(getAdvertisingPostsRequest.type, getPostsWorker);

  yield takeEvery(getAdvertisingPostRequest.type, getPostWorker);

  yield takeEvery(getAdvertisingReportsRequest.type, getReportsWorker);

  yield takeEvery(getAdvertisingRevenueEventsRequest.type, getRevenueEventsWorker);

  yield takeEvery(getAdvertisingRevenueEventRequest.type, getRevenueEventWorker);

  yield takeEvery(getAdvertisingWalletCreditsRequest.type, getWalletCreditsWorker);

  yield takeEvery(getAdvertisingWalletCreditRequest.type, getWalletCreditWorker);
}
